import { useState } from "react";
import { Ban, Loader2, Stamp, Lock } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TooltipButton } from "@/components/common/Tooltips";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { mxn, formatearPlazo } from "@/lib/format";
import { toast } from "sonner";
import {
  estaSellada, estaCancelada, estaDesembolsada, esPendienteComite,
} from "@/lib/selloSolicitud";

const MIN_MOTIVO = 10;

/** Envío al Comité (= sellado) y cancelación de la solicitud. Sustituye al bote de basura:
 *  una solicitud nunca se borra, se cancela con motivo y queda como evidencia. */
export function AccionesSolicitud({ solicitud: s, onDone }) {
  const { user } = useAuth();
  const [modo, setModo] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  if (!s?.id) return null;

  const sellada = estaSellada(s);
  const cancelada = estaCancelada(s);
  const puedeEnviar = !cancelada && !sellada && esPendienteComite(s);
  const puedeCancelar = user?.role === "admin_entidad" && !cancelada && !estaDesembolsada(s);

  const cerrar = () => { setModo(null); setMotivo(""); };

  const enviar = async () => {
    setBusy(true);
    try {
      await api.post(`/solicitudes/${s.id}/enviar-comite`);
      toast.success("Enviada al Comité. Sus condiciones quedaron selladas.");
      cerrar();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar al Comité");
    } finally { setBusy(false); }
  };

  const cancelar = async () => {
    const m = motivo.trim();
    if (m.length < MIN_MOTIVO) {
      toast.error(`Escribe el motivo de la cancelación (mínimo ${MIN_MOTIVO} caracteres)`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/solicitudes/${s.id}/cancelar`, { motivo: m });
      toast.success(data?.credito_cancelado
        ? `Solicitud y crédito ${data.credito_cancelado} cancelados. Nada se borró.`
        : "Solicitud cancelada. Se conserva como evidencia y el expediente sigue completo.");
      cerrar();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cancelar la solicitud");
    } finally { setBusy(false); }
  };

  return (
    <>
      {cancelada && (
        <span data-testid={`solicitud-cancelada-${s.id}`}
              title={s.cancelado_motivo ? `Motivo: ${s.cancelado_motivo}` : undefined}
              className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600">
          <Ban className="h-3 w-3" /> Cancelada
        </span>
      )}
      {!cancelada && sellada && (
        <span data-testid={`solicitud-sellada-${s.id}`}
              title={`Condiciones selladas al enviar al Comité${s.sello?.enviada_por ? ` por ${s.sello.enviada_por}` : ""}`}
              className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
          <Lock className="h-3 w-3" /> Sellada
        </span>
      )}
      {puedeEnviar && (
        <TooltipButton tooltip="Enviar al Comité: sella cliente, producto, montos, plazo, periodicidad, tasas, comisión e IVA"
          data-testid={`enviar-comite-btn-${s.id}`} variant="outline" size="sm"
          className="h-8 gap-1.5 border-primary/30 px-2.5 text-primary hover:bg-primary/5"
          onClick={() => setModo("enviar")}>
          <Stamp className="h-4 w-4" /> Enviar al Comité
        </TooltipButton>
      )}
      {puedeCancelar && (
        <TooltipButton tooltip="Cancelar la solicitud (solo administrador; pide motivo y queda en bitácora)"
          data-testid={`cancelar-solicitud-btn-${s.id}`} variant="outline" size="sm"
          className="h-8 gap-1.5 border-red-200 px-2.5 text-red-600 hover:bg-red-50 hover:text-red-700"
          onClick={() => setModo("cancelar")}>
          <Ban className="h-4 w-4" /> Cancelar
        </TooltipButton>
      )}

      <AlertDialog open={modo === "enviar"} onOpenChange={(v) => !v && cerrar()}>
        <AlertDialogContent data-testid="enviar-comite-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Enviar al Comité — {s.cliente || "solicitud"}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>Al enviarla, estas condiciones quedan <strong>selladas</strong> y nadie las modifica:</p>
                <ul className="list-disc space-y-0.5 pl-5 text-[13px] text-slate-600">
                  <li>Cliente y producto: <strong>{s.cliente || "—"}</strong> · {s.producto || "—"}</li>
                  <li>Monto solicitado <strong>{mxn(s.monto_solicitado)}</strong> · autorizado <strong>{mxn(s.monto_autorizado)}</strong></li>
                  <li>Plazo <strong>{formatearPlazo(s.plazo, s.periodicidad)}</strong></li>
                  <li>Tasa ordinaria <strong>{s.tasa_ordinaria ?? "—"}%</strong> · moratoria <strong>{s.tasa_moratoria ?? "—"}%</strong></li>
                  <li>Comisión por apertura <strong>{s.comision_apertura ?? "—"}</strong> · IVA <strong>{s.iva ?? "—"}%</strong></li>
                </ul>
                <p className="text-[13px] text-slate-500">
                  El Comité <strong>aprueba o rechaza</strong>, no cambia montos. Si hay que
                  cambiar condiciones, se cancela esta solicitud y se captura una nueva.
                  Siguen abiertos: documentos, nueva consulta SIC, comentarios, cuenta y medio
                  de dispersión, garantía descriptiva y obligados solidarios.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="enviar-comite-volver">Volver</AlertDialogCancel>
            <AlertDialogAction data-testid="confirmar-enviar-comite" disabled={busy}
              onClick={(e) => { e.preventDefault(); enviar(); }}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Sellar y enviar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={modo === "cancelar"} onOpenChange={(v) => !v && cerrar()}>
        <AlertDialogContent data-testid="cancelar-solicitud-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar la solicitud de {s.cliente || "—"}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  La cancelación es <strong>lógica</strong>: nada se borra. La solicitud queda
                  en «Cancelado» con tu nombre, la fecha y el motivo en la bitácora.
                </p>
                <ul className="list-disc space-y-0.5 pl-5 text-[13px] text-slate-600">
                  <li>El <strong>expediente y los documentos del cliente se conservan</strong> para futuros créditos.</li>
                  <li>Si ya generó crédito pendiente de desembolso, se cancela con ella: saldo en cero, tabla de proyección y garantía marcadas como canceladas.</li>
                  <li>El folio del crédito <strong>no se reutiliza</strong>.</li>
                </ul>
                <p className="text-[13px] text-slate-500">
                  Solo aplica <strong>antes del desembolso</strong>. Después de entregar el
                  dinero esta vía queda cerrada.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Label className="mb-1 block text-xs">
              Motivo de la cancelación <span className="text-red-500">*</span>
            </Label>
            <Textarea data-testid="cancelar-solicitud-motivo" rows={2} value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej. el cliente desistió de la operación y pidió cancelar el trámite" />
            <p className="mt-1 text-[11px] text-slate-400">
              Obligatorio, mínimo {MIN_MOTIVO} caracteres. Queda en la bitácora de la entidad.
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="cancelar-solicitud-volver">Volver</AlertDialogCancel>
            <AlertDialogAction data-testid="confirmar-cancelar-solicitud"
              disabled={busy || motivo.trim().length < MIN_MOTIVO}
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => { e.preventDefault(); cancelar(); }}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Confirmar cancelación
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default AccionesSolicitud;
